import type { LeadNaLista } from '@baiocchi/shared'
import {
  Alerta,
  Botao,
  Cartao,
  Entrada,
  EstadoVazio,
  Etiqueta,
  Menu,
  RodapePaginacao,
  TabelaDados,
  cn,
  useAvisos,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { leadsApi } from '../../api/leads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import {
  EtiquetaAtrasado,
  EtiquetaPercepcao,
  EtiquetaResponsavel,
  EtiquetaStatus,
  EtiquetaTemperatura,
} from '../../componentes/etiquetas-dominio'
import {
  IconeBaixar,
  IconeBusca,
  IconeFiltro,
  IconeMais,
  IconeTransferir,
} from '../../componentes/icones'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { useDebounce } from '../../ganchos/usar-debounce'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { usePaginacaoCursor } from '../../ganchos/usar-paginacao-cursor'
import { CabecalhoPagina } from '../../layout/layout-app'
import {
  formatarDataHora,
  formatarTelefone,
  formatarTempoRelativo,
} from '../../utilitarios/formatar'
import { GavetaFiltrosLeads } from './filtros-leads'
import { ModalTransferenciaLote } from './transferencia-lote'

/** Toggles da barra secundária da TELA-04. */
const TOGGLES = [
  { chave: 'ocultarFinalizados', rotulo: 'Ocultar finalizados', valor: 'true' },
  { chave: 'atrasados', rotulo: 'Só atrasados', valor: 'true' },
  { chave: 'semPrimeiroAtendimento', rotulo: 'Sem 1º atendimento', valor: 'true' },
] as const

/**
 * TELA-04 — Lista de leads.
 *
 * Paginação, ordenação e filtro são **do servidor**. Não há ordenação no cliente
 * porque o cursor é keyset sobre a ordem do servidor: reordenar aqui daria uma
 * ordem diferente da que a página seguinte traria.
 *
 * `incluirTotal` só é pedido quando há filtro de período (§6.3) — `COUNT(*)` custa
 * quase o mesmo que a consulta, e a API recusa com `422` fora dessa condição.
 */
export function PaginaListaLeads() {
  const { filtros, definir, limpar, alternarOrdenacao, ordenacao } = useFiltrosUrl()
  const navegar = useNavigate()
  const { pode } = useSessao()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()

  const [gavetaFiltros, setGavetaFiltros] = useState(false)
  const [selecionados, setSelecionados] = useState<string[]>([])
  const [modalLote, setModalLote] = useState<'transferir' | 'equipe' | null>(null)
  const [buscaLocal, setBuscaLocal] = useState(filtros.busca ?? '')
  const buscaAtrasada = useDebounce(buscaLocal)

  const paginacao = usePaginacaoCursor(Number(filtros.limite ?? 50))

  // Espelha a busca com atraso na URL. A lista de dependências é `buscaAtrasada` e
  // só ela de propósito: o efeito **escreve** em `filtros.busca` pelo `definir`, e
  // incluir `filtros.busca` o faria reagir à própria escrita. `definir` e
  // `paginacao` são estáveis para o que este efeito usa.
  useEffect(() => {
    if ((filtros.busca ?? '') !== buscaAtrasada) {
      definir({ busca: buscaAtrasada || undefined })
      paginacao.reiniciar()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaAtrasada])

  const temPeriodo = Boolean(filtros.chegadaEmDe || filtros.chegadaEmAte)

  const consultaFiltros = useMemo(
    () => ({
      ...filtros,
      limite: paginacao.limite,
      cursor: paginacao.cursor,
      ordenar: filtros.ordenar ?? '-chegadaEm',
      ...(temPeriodo ? { incluirTotal: 'true' } : {}),
    }),
    [filtros, paginacao.limite, paginacao.cursor, temPeriodo],
  )

  const consulta = useQuery({
    queryKey: chaves.leads.lista(consultaFiltros),
    queryFn: ({ signal }) => leadsApi.listar(consultaFiltros, signal),
    staleTime: 15_000,
  })

  const itens = consulta.data?.dados ?? []
  const filtrosAtivos = contarFiltrosAtivos(filtros)

  const colunas = useMemo<ReadonlyArray<ColunaTabela<LeadNaLista>>>(
    () => [
      {
        chave: 'chegadaEm',
        cabecalho: 'Chegada',
        ordenavel: true,
        classe: 'whitespace-nowrap',
        celula: (lead) => (
          <span className="text-xs tabular-nums text-conteudo-suave">
            {formatarDataHora(lead.chegadaEm)}
          </span>
        ),
      },
      {
        chave: 'ultimaInteracaoEm',
        cabecalho: 'Última interação',
        ordenavel: true,
        ocultarAte: 'lg',
        classe: 'whitespace-nowrap',
        celula: (lead) => (
          <span
            className={cn(
              'text-xs',
              lead.ultimaInteracaoEm ? 'text-conteudo-suave' : 'text-conteudo-fraco',
            )}
            title={formatarDataHora(lead.ultimaInteracaoEm)}
          >
            {lead.ultimaInteracaoEm ? formatarTempoRelativo(lead.ultimaInteracaoEm) : 'nunca'}
          </span>
        ),
      },
      {
        chave: 'clienteNome',
        cabecalho: 'Lead',
        ordenavel: true,
        celula: (lead) => (
          <div className="flex min-w-0 flex-col">
            <Link
              to={`/leads/${lead.uuid}`}
              onClick={(evento) => evento.stopPropagation()}
              className="truncate font-medium text-marca-600 hover:underline"
            >
              {lead.cliente.nome}
            </Link>
            <span className="truncate text-xs tabular-nums text-conteudo-fraco">
              {formatarTelefone(lead.cliente.contatoPrincipal)}
            </span>
          </div>
        ),
      },
      {
        chave: 'produto',
        cabecalho: 'Produto',
        ocultarAte: 'xl',
        celula: (lead) => (
          <span className="text-xs text-conteudo-suave">
            {lead.produtoAssociado?.titulo ?? '—'}
          </span>
        ),
      },
      {
        chave: 'responsavel',
        cabecalho: 'Responsável',
        ocultarAte: 'md',
        celula: (lead) => <EtiquetaResponsavel responsavel={lead.responsavel} />,
      },
      {
        chave: 'unidade',
        cabecalho: 'Unidade',
        ocultarAte: 'xl',
        celula: (lead) => <span className="text-xs">{lead.unidade.titulo}</span>,
      },
      {
        chave: 'equipe',
        cabecalho: 'Equipe',
        ocultarAte: 'xl',
        celula: (lead) => <span className="text-xs">{lead.equipe?.titulo ?? '—'}</span>,
      },
      {
        chave: 'pontoConversao',
        cabecalho: 'Ponto de conversão',
        ocultarAte: 'lg',
        celula: (lead) =>
          lead.pontoConversao ? (
            <Etiqueta tom="marca">{lead.pontoConversao.titulo}</Etiqueta>
          ) : (
            <span className="text-xs text-conteudo-fraco">—</span>
          ),
      },
      {
        chave: 'temperatura',
        cabecalho: 'Temperatura',
        ordenavel: true,
        ocultarAte: 'lg',
        celula: (lead) => <EtiquetaTemperatura temperatura={lead.temperatura} />,
      },
      {
        chave: 'percepcao',
        cabecalho: 'Percepção',
        ocultarAte: 'xl',
        celula: (lead) => <EtiquetaPercepcao percepcao={lead.percepcao} />,
      },
      {
        chave: 'status',
        cabecalho: 'Situação',
        ordenavel: true,
        celula: (lead) => (
          <div className="flex flex-wrap items-center gap-1">
            <EtiquetaStatus status={lead.status} />
            <EtiquetaAtrasado atrasado={lead.atrasado} />
          </div>
        ),
      },
    ],
    [],
  )

  const podeLote = pode('LEAD_TRANSFERIR', 'LEAD_TRANSFERIR_LOTE')

  return (
    <>
      <CabecalhoPagina
        titulo="Leads"
        descricao="Todos os leads do seu escopo, com filtros e ordenação no servidor."
        acoes={
          <>
            {pode('LEAD_CRIAR') && (
              <Botao
                onClick={() => navegar('/leads/novo')}
                iconeEsquerda={<IconeMais className="h-4 w-4" />}
              >
                Novo lead
              </Botao>
            )}
            <Botao
              variante="contorno"
              onClick={() => setGavetaFiltros(true)}
              iconeEsquerda={<IconeFiltro className="h-4 w-4" />}
            >
              Filtrar
              {filtrosAtivos > 0 ? (
                <span className="ml-1.5 rounded-full bg-marca-500 px-1.5 text-[0.625rem] font-bold text-white">
                  {filtrosAtivos}
                </span>
              ) : null}
            </Botao>
            <Menu
              alinhamento="direita"
              gatilho={
                <Botao variante="contorno">
                  Ações{selecionados.length > 0 ? ` (${selecionados.length})` : ''}
                </Botao>
              }
              itens={[
                {
                  rotulo: 'Transferir responsável',
                  icone: <IconeTransferir className="h-4 w-4" />,
                  desabilitado: !podeLote || selecionados.length === 0,
                  motivoBloqueio: !podeLote
                    ? 'Seu perfil não permite transferir leads em lote.'
                    : 'Selecione ao menos um lead.',
                  onSelecionar: () => setModalLote('transferir'),
                },
                {
                  rotulo: 'Mudar de equipe',
                  icone: <IconeTransferir className="h-4 w-4" />,
                  desabilitado: !podeLote || selecionados.length === 0,
                  motivoBloqueio: !podeLote
                    ? 'Seu perfil não permite mudar leads de equipe.'
                    : 'Selecione ao menos um lead.',
                  onSelecionar: () => setModalLote('equipe'),
                },
                {
                  rotulo: 'Exportar resultado filtrado',
                  icone: <IconeBaixar className="h-4 w-4" />,
                  desabilitado: true,
                  motivoBloqueio:
                    'POST /leads/exportar ainda não foi publicado pela API — exportação exige job assíncrono e link expirável (RN-32A).',
                  onSelecionar: () => undefined,
                },
              ]}
            />
          </>
        }
      />

      <Cartao className="mb-4" semPadding>
        <div className="flex flex-wrap items-center gap-3 p-3 sm:p-4">
          <div className="min-w-56 flex-1">
            <Entrada
              value={buscaLocal}
              onChange={(evento) => setBuscaLocal(evento.target.value)}
              placeholder="Buscar por nome, telefone ou e-mail…"
              prefixo={<IconeBusca className="h-4 w-4" />}
              aria-label="Buscar leads"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {TOGGLES.map((toggle) => {
              const ativo = filtros[toggle.chave] === toggle.valor
              return (
                <button
                  key={toggle.chave}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => {
                    definir({ [toggle.chave]: ativo ? undefined : toggle.valor })
                    paginacao.reiniciar()
                  }}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    ativo
                      ? 'border-marca-500 bg-marca-500 text-white'
                      : 'border-borda-forte bg-superficie text-conteudo-suave hover:bg-superficie-2',
                  )}
                >
                  {toggle.rotulo}
                </button>
              )
            })}

            {filtrosAtivos > 0 && (
              <Botao
                variante="fantasma"
                tamanho="sm"
                onClick={() => {
                  limpar()
                  setBuscaLocal('')
                  paginacao.reiniciar()
                }}
              >
                Remover filtros
              </Botao>
            )}
          </div>
        </div>
      </Cartao>

      {selecionados.length > 0 && (
        <Alerta tom="info" className="mb-3">
          {selecionados.length} lead(s) selecionado(s) nesta página. As ações em lote valem apenas
          para a seleção visível — a exportação, quando existir, usará o filtro inteiro.
        </Alerta>
      )}

      <Cartao semPadding>
        <TabelaDados
          colunas={colunas}
          itens={itens}
          chaveDe={(lead) => lead.uuid}
          ordenacao={ordenacao}
          onOrdenar={(campo) => {
            alternarOrdenacao(campo)
            paginacao.reiniciar()
          }}
          selecao={
            podeLote
              ? {
                  selecionados,
                  onAlterar: setSelecionados,
                  // Lead terminal não é transferível (RN-26A): deixá-lo
                  // selecionável só produziria um item em `ignorados[]`.
                  bloqueado: (lead) =>
                    lead.status === 'VENDA_REALIZADA' ||
                    lead.status === 'VENDA_NAO_REALIZADA' ||
                    lead.status === 'FINALIZADO',
                }
              : undefined
          }
          carregando={consulta.isPending}
          erro={
            consulta.isError ? (
              <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
            ) : undefined
          }
          realce={(lead) => lead.atrasado}
          onClicarLinha={(lead) => navegar(`/leads/${lead.uuid}`)}
          vazio={
            <EstadoVazio
              titulo="Nenhum lead encontrado"
              descricao={
                filtrosAtivos > 0
                  ? 'Nenhum lead do seu escopo atende a estes filtros.'
                  : 'Cadastre o primeiro lead para começar a atender.'
              }
              acao={
                filtrosAtivos > 0 ? (
                  <Botao variante="contorno" onClick={() => limpar()}>
                    Remover filtros
                  </Botao>
                ) : pode('LEAD_CRIAR') ? (
                  <Botao onClick={() => navegar('/leads/novo')}>Novo lead</Botao>
                ) : undefined
              }
            />
          }
          cartaoMobile={(lead) => <CartaoLeadMobile lead={lead} />}
        />

        <RodapePaginacao
          limite={paginacao.limite}
          onAlterarLimite={(limite) => {
            paginacao.alterarLimite(limite)
            definir({ limite })
          }}
          quantidadeNaPagina={itens.length}
          indiceInicial={itens.length === 0 ? 0 : paginacao.indiceInicial}
          total={consulta.data?.paginacao.total ?? null}
          temAnterior={paginacao.temAnterior}
          temProximo={consulta.data?.paginacao.temMais ?? false}
          onAnterior={paginacao.voltar}
          onProximo={() => paginacao.avancar(consulta.data?.paginacao.proximoCursor ?? null)}
        />
      </Cartao>

      {!temPeriodo && (
        <p className="mt-3 text-xs text-conteudo-fraco">
          O total de registros só é calculado quando há filtro de período de chegada — contar a
          base inteira custa quase o mesmo que a consulta.
        </p>
      )}

      <GavetaFiltrosLeads
        aberta={gavetaFiltros}
        onFechar={() => setGavetaFiltros(false)}
        filtros={filtros}
        onAplicar={(novos) => {
          definir(novos)
          setBuscaLocal(novos.busca ?? '')
          paginacao.reiniciar()
        }}
        onLimpar={() => {
          limpar()
          setBuscaLocal('')
          paginacao.reiniciar()
        }}
      />

      <ModalTransferenciaLote
        modo={modalLote}
        leadUuids={selecionados}
        onFechar={() => setModalLote(null)}
        onConcluido={(resultado) => {
          setModalLote(null)
          setSelecionados([])
          void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
          void clienteConsulta.invalidateQueries({ queryKey: chaves.painel.todos })
          avisos.sucesso(
            `${resultado.transferidos} de ${resultado.total} lead(s) atualizados`,
            resultado.ignorados.length > 0
              ? `${resultado.ignorados.length} ignorado(s): ${resultado.ignorados
                  .slice(0, 2)
                  .map((item) => item.motivo)
                  .join('; ')}`
              : undefined,
          )
        }}
      />
    </>
  )
}

function CartaoLeadMobile({ lead }: { lead: LeadNaLista }) {
  return (
    <Link
      to={`/leads/${lead.uuid}`}
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3 transition-colors',
        lead.atrasado ? 'border-perigo/30 bg-perigo-suave' : 'border-borda bg-superficie',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-conteudo">{lead.cliente.nome}</p>
          <p className="text-xs tabular-nums text-conteudo-fraco">
            {formatarTelefone(lead.cliente.contatoPrincipal)}
          </p>
        </div>
        <EtiquetaStatus status={lead.status} />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <EtiquetaTemperatura temperatura={lead.temperatura} />
        <EtiquetaResponsavel responsavel={lead.responsavel} />
        <EtiquetaAtrasado atrasado={lead.atrasado} />
      </div>

      <div className="flex flex-wrap justify-between gap-2 text-xs text-conteudo-fraco">
        <span>{lead.equipe?.titulo ?? 'Sem equipe'}</span>
        <span className="tabular-nums">
          {lead.ultimaInteracaoEm
            ? `interação ${formatarTempoRelativo(lead.ultimaInteracaoEm)}`
            : 'nunca atendido'}
        </span>
      </div>
    </Link>
  )
}

/** Conta filtros de conteúdo — paginação e ordenação não são "filtro ativo". */
function contarFiltrosAtivos(filtros: Record<string, string>): number {
  const ignorar = new Set(['ordenar', 'limite', 'cursor'])
  return Object.entries(filtros).filter(([chave, valor]) => !ignorar.has(chave) && valor !== '')
    .length
}
